import { loadingFactsService } from './loading-facts.service';

const FLUSH_DELAY_MS = 5000;

/**
 * Singleton batcher for marking loading-facts as "viewed ≥8s" on the server.
 * Coalesces fact ids into one POST per 5s window — UI hooks just call
 * {@link enqueue}; flushing is automatic. {@link flushNow} drains the buffer
 * immediately (on component unmount), {@link clear} discards it without a
 * network call (on logout — the next user must not inherit the prior user's
 * pending seen ids).
 *
 * Lives outside the React tree so the buffer survives RenderProgress
 * re-mounts during a single auth session. Wipe is wired into
 * `cacheInvalidation.afterLogout`.
 */
class SeenFactsBatcher {
  private readonly pending = new Set<string>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  enqueue(factId: string): void {
    if (!factId || this.pending.has(factId)) return;
    this.pending.add(factId);
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.flushNow(), FLUSH_DELAY_MS);
  }

  flushNow(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pending.size === 0) return;
    const ids = Array.from(this.pending);
    this.pending.clear();
    loadingFactsService.markSeen(ids).catch(() => {
      // Non-critical: API logs as debug (silentErrors). The next GET reseeds
      // server seen-snapshot, so a missed batch only risks one duplicate fact.
    });
  }

  clear(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.pending.clear();
  }
}

export const seenFactsBatcher = new SeenFactsBatcher();
